/**
 * Table is used to show data containing columns and rows.
 * The contents (data) of the table is injected from the "container" using this "component".
 * This component can be reused  by changing the given data.
 */
/* eslint-disable */
import * as React from "react";
import TanStackTableWrapper, { Filter } from "./Migration/TanStackTableWrapper";
import classNames from "classnames";
import styles from "./Table.module.scss";
import CheckedMark from "../CheckedMark/CheckedMark";

// Table props, these need to be injected from the using container.
export type TableProps = {
  rows: any[];
  columns: State.Column[];
  isHidingTextActive: boolean;
  handleRowSelection: (row: any, getOption?: Api.OptionsGrud) => void;
  getOption?: Api.OptionsGrud;
  isRowSelected: (row: any) => boolean;  // Fixed: should return boolean, not void
  pageSize: State.PageSize;
  disablePagination?: boolean;
  loading?: boolean;
  loadingText?: string;
  setHeight?: string;
  startingPage?: number;
  onPageChange?: (page: number) => void;
  // Because Table component is stateless, properties 'handleFilterChange'
  // and 'filtered' are for handling the column state in the parent container.
  handleFilterChange?: (changedFilter: Filter[]) => void;
  filtered?: Filter[];
  // Legacy react-table props for backward compatibility
  style?: React.CSSProperties;
  className?: string;
  getTdProps?: (state?: any, rowInfo?: any, column?: any) => any;
  getTrProps?: (state?: any, rowInfo?: any) => any;
  // Additional legacy props
  filterable?: boolean;
  showPagination?: boolean;
  showPageSizeOptions?: boolean;
  defaultFilterMethod?: (filter: any, row: any) => boolean;
  data?: any[];
  minRows?: number;
};

/**
 * TanStack Table Wrapper Component
 * 
 * This component is stateless with no lifecycle functions. It returns JSX:Element for rendering.
 * Uses TanStack Table v8 internally with backward compatibility layer.
 */
const Table: React.FunctionComponent<TableProps> = (props) => {
    return <TanStackTableWrapper key={`table-ps-${props.pageSize}`} {...props} />;
};

// Export as TanStackTable for clarity (backward compatible)
export default Table;
export { Table as TanStackTable };

export const createColumn = (
  name: string,
  accessor: (fault: any) => any,
  ownAccessorName: string,
  isVisible: boolean,
  width?: number,
  minWidth?: number,
  hideHeader?: boolean,
  textColor?: string,
  textAlign?: string
): State.Column => {
  return {
    name,
    Header: hideHeader ? () => null : name,
    filterable: !hideHeader,
    id: name,
    accessor,
    ownAccessor: accessor,
    ownAccessorName,
    isVisible,
    hasDropdownFiltering: false,
    width,
    minWidth: minWidth ? minWidth : 100,
    getProps: () => {
      return {
        style: {
          color: !!textColor ? textColor : "black",
          justifyContent: !!textAlign ? textAlign : "left"
        }
      };
    }
  };
};

export const createPercentageColumn = (
  name: string,
  accessor: (fault: any) => any,
  ownAccessorName: string,
  isVisible: boolean,
  width?: number,
  minWidth?: number,
  hideHeader?: boolean,
  textColor?: string,
  textAlign?: string
): State.Column => {
  return {
    name,
    Header: hideHeader ? () => null : name,
    filterable: !hideHeader,
    id: name,
    accessor,
    ownAccessor: accessor,
    sortMethod: (a, b) => Number(a.replace("%", "")) - Number(b.replace("%", "")),
    ownAccessorName,
    isVisible,
    hasDropdownFiltering: false,
    width,
    minWidth: minWidth ? minWidth : 100,
    getProps: () => {
      return {
        style: {
          color: !!textColor ? textColor : "black",
          justifyContent: !!textAlign ? textAlign : "left"
        }
      };
    }
  };
};

export const createEditableColumn = (
  name: string,
  accessor: (fault: any) => any,
  ownAccessorName: string,
  isVisible: boolean,
  width?: number,
  minWidth?: number,
  headerClassName?: string
): State.Column => {
  return {
    name,
    Header: name,
    id: name,
    headerClassName,
    accessor,
    ownAccessor: accessor,
    ownAccessorName,
    isVisible,
    hasDropdownFiltering: false,
    width,
    minWidth: minWidth ? minWidth : 100
  };
};

export const createCheckboxColumn = (
  name: string,
  accessor: (fault: any) => any,
  ownAccessorName: string,
  isVisible: boolean,
  width?: number,
  minWidth?: number,
  maxWidth?: number
): State.Column => {
  return {
    name,
    Header: name,
    id: name,
    accessor,
    ownAccessor: accessor,
    ownAccessorName,
    isVisible,
    hasDropdownFiltering: false,
    width: width ?? 60,
    minWidth: minWidth ? minWidth : 30,
    maxWidth: maxWidth ?? (width ? width + 20 : 100),
    filterable: false,
    Cell: (props: any) => (
      <span className={styles.checkbox}>
        {props.value && <CheckedMark id={name.replace(/\s/g, "") + "table"} />}
      </span>
    )
  };
};

export const createButtonColumn = (
  name: string,
  accessor: (fault: any) => any,
  ownAccessorName: string,
  isVisible: boolean,
  onEditClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => void,
  width?: number,
  minWidth?: number,
  maxWidth?: number,
  hideHeader?: boolean
): State.Column => {
  return {
    Header: hideHeader ? () => null : "",
    name: "",
    id: name,
    accessor,
    ownAccessor: accessor,
    ownAccessorName,
    isVisible,
    hasDropdownFiltering: false,
    width,
    minWidth: minWidth ? minWidth : 30,
    maxWidth,
    filterable: false,
    Cell: ({ row }) => onEditClick && <button onClick={e => onEditClick(e, row)}>Edit</button>
  };
};

const multiValueDropdownFilterMethod = () => {
  return (filter: { value: string; id: string }, row: Record<string, unknown>, column: State.Column) => {
    let filterValue = filter.value;
    if (filter !== undefined) {
      const exists = column.filterDropdownOptions?.some(
        (eachOption) => filter.value === eachOption
      );
      if (!exists) {
        filterValue = (column.filterDropdownOptions?.length ?? 0) > 1 ? "all" : String(column.filterDropdownOptions?.[0] ?? "all");
      }
    }
    if (filterValue === "all") {
      return true;
    }
    const cellValue: string = String(row[filter.id] || "");
    return cellValue.split(", ").some((val: string) => val.trim() === filterValue);
  };
};

/**
 * Overrides column's filtering in react-table
 *
 * TODO: Comment why override and how does this differ from the previous function.
 */
const dropdownFilterMethod = () => {
  return (filter: any, row: any, column: any) => {
    if(filter !== undefined) {
      const filterNewOptionList = column.filterDropdownOptions.findIndex((eachOption: any)=> filter.value === eachOption);
      if(filterNewOptionList === -1) {
        filter.value = column.filterDropdownOptions.length>1 ? "all" : column.filterDropdownOptions[0];
      }
    }
    if (filter.value === "all") {
      return true;
    }
    return row[filter.id] === filter.value;
  };
};

export const createDropdownColumn = (
  name: string,
  accessor: (fault: any) => any,
  ownAccessorName: string,
  isVisible: boolean,
  width?: number,
  minWidth?: number,
  maxWidth?: number,
  filterDropdownOptions?: (string | number)[],
  textAlign?: string
): State.Column => {
  return {
    name,
    Header: name,
    id: name,
    accessor,
    ownAccessor: accessor,
    ownAccessorName,
    isVisible,
    hasDropdownFiltering: true,
    width,
    minWidth: minWidth ? minWidth : 100,
    maxWidth,
    filterMethod: dropdownFilterMethod(),
    filterDropdownOptions,
    getProps: () => {
      return {
        style: {
          justifyContent: !!textAlign ? textAlign : "left"
        }
      };
    }
  };
};

export const createMultiValueDropdownColumn = (
  name: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  accessor: (row: any) => string | number,
  ownAccessorName: string,
  isVisible: boolean,
  width?: number,
  minWidth?: number,
  maxWidth?: number,
  filterDropdownOptions?: (string | number)[],
  textAlign?: string
): State.Column => {
  return {
    name,
    Header: name,
    id: name,
    accessor,
    ownAccessor: accessor,
    ownAccessorName,
    isVisible,
    hasDropdownFiltering: true,
    isMultiValueDropdown: true,
    width,
    minWidth: minWidth ? minWidth : 100,
    maxWidth,
    filterMethod: multiValueDropdownFilterMethod(),
    filterDropdownOptions,
    getProps: () => {
      return {
        style: {
          justifyContent: !!textAlign ? textAlign : "left"
        }
      };
    }
  };
};
